import { useEffect } from 'react';
import { CheckCircle, Phone, ShoppingBag, Clock, MessageCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';

const OrderConfirmation = ({
  orderId,
  total,
  estimatedDelivery,
  vendorName,
  vendorContact,
  onContinueShopping,
  onViewOrder,
}) => {
  useEffect(() => {
    // Trigger confetti when component mounts
    triggerConfetti();

    // Clean up any ongoing animations when component unmounts
    return () => {
      confetti.reset();
    };
  }, []);

  const minOrderId = `${orderId.slice(0, 3)}${orderId.slice(orderId.length - 6, orderId.length - 1)}`;
  console.log(minOrderId);

  const triggerConfetti = () => {
    const scalar = 2;
    const unicorn = confetti.shapeFromText({ text: '🦄', scalar });
    const party = confetti.shapeFromText({ text: '🎉', scalar });
    const thumbsUp = confetti.shapeFromText({ text: '👍', scalar });

    const defaults = {
      spread: 360,
      ticks: 60,
      gravity: 0,
      decay: 0.96,
      startVelocity: 20,
      shapes: [unicorn, party, thumbsUp],
      scalar,
    };

    const shoot = () => {
      confetti({
        ...defaults,
        particleCount: 30,
      });

      confetti({
        ...defaults,
        particleCount: 5,
      });

      confetti({
        ...defaults,
        particleCount: 15,
        scalar: scalar / 2,
        shapes: ['circle'],
      });
    };

    // Shoot confetti multiple times with delays
    setTimeout(shoot, 0);
    setTimeout(shoot, 100);
    setTimeout(shoot, 200);
    setTimeout(shoot, 300);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full max-w-md mx-auto">
      <motion.div
        className="bg-white  overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-3 text-center text-white">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
            className="bg-white rounded-full w-16 h-16 mx-auto mb-2 flex items-center justify-center"
          >
            <CheckCircle className="h-10 w-10 text-green-500" />
          </motion.div>
          <h1 className="text-2xl font-bold mb-1">Order Confirmed!</h1>
          <p className="text-green-100">Your order has been placed successfully</p>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-gray-500">Order ID</h2>
              <p className="font-semibold text-gray-800">{minOrderId}</p>
            </div>

            <div className="flex justify-between items-center mb-3">
              <h2 className="text-gray-500">Order value</h2>
              <p className="font-semibold text-gray-800">₹{total}</p>
            </div>
            <div className="flex text-balance text-center items-start mb-1">
              <p>Your order is received and will be delivered to you by {estimatedDelivery}</p>
            </div>
          </div>

          <div className="border-t border-dashed my-4 pt-4">
            <h3 className="font-medium text-gray-800 mb-3">Your Local Vendor</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-gray-800">{vendorName}</p>
                </div>

                <div className="flex items-center gap-2">
                  {vendorContact && (
                    <a
                      href={`https://api.whatsapp.com/send/?phone=91${vendorContact}&text=Hello i have placed the order for you&type=phone_number&app_absent=0`}
                      className="p-2.5 bg-green-500 rounded-full text-white"
                    >
                      <MessageCircleIcon className="h-4 w-4" />
                    </a>
                  )}
                  {vendorContact && (
                    <a
                      href={`tel:${vendorContact}`}
                      className="p-2.5 bg-green-500 rounded-full text-white"
                    >
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6 mt-4">
            <p className="text-sm text-center text-gray-500 mb-4">
              Thank you for supporting your local economy!
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => onViewOrder(orderId)}
                className="py-2.5 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <Clock className="w-4 h-4 mr-2" />
                Track Order
              </button>
              <button
                onClick={onContinueShopping}
                className="py-2.5 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Continue
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderConfirmation;
